<template>
	<fieldset :class="{ 'language-validation-error': validationError }">
		<legend>{{ label ? label : 'Language Proficiency' }}</legend>
		<div v-for="langRow in languageRows" :key="langRow.code" class="language-row">
			<span class="language-label">{{ langRow.label }}</span>
			<span class="language-level">{{ langRow.level }}</span>
			<span class="language-remove" @click="deleteLanguageBtn(langRow.code)"><i class="dx-icon dx-icon-trash"></i></span>
		</div>
		<DxBox :direction="!isTiny ? 'row' : 'col'">
			<DxItem :ratio="!isTiny ? 1 : 0" base-size="auto">
				<DxSelectBox :data-source="languagesData" label="Language (type to search)" value-expr="code" :display-expr="languageDisplayExpr" :search-enabled="true" v-model:value="addLanguageCode" />
			</DxItem>
			<DxItem :base-size="10" />
			<DxItem :base-size="!isTiny ? 200 : 'auto'">
				<DxSelectBox :data-source="languageLevels" label="Proficiency" v-model:value="addLanguageLevel" />
			</DxItem>
			<DxItem :base-size="10" />
			<DxItem :base-size="!isTiny ? 80 : 'auto'">
				<DxButton text="Add" styling-mode="solid" type="default" @click="addLanguageBtn" />
			</DxItem>
		</DxBox>
		<DxValidator :adapter="validationAdapter" :validation-group="validationGroup">
			<DxCustomRule :validation-callback="validateCallback" message="You must specify at least one language." />
		</DxValidator>
		<div v-if="validationError" class="language-validation-error">You must specify at least one language.</div>
	</fieldset>
</template>

<script setup>
import { ref, defineProps, defineEmits, toRefs, onMounted, onUnmounted } from 'vue';
import languages from '../data/languages.js';
import { DxBox, DxItem } from 'devextreme-vue/box';
import DxSelectBox from 'devextreme-vue/select-box';
import DxButton from 'devextreme-vue/button';
import DxValidator, { DxCustomRule } from 'devextreme-vue/validator';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import { sizes as mediaSizes, subscribe as subscribeMediaQuery, unsubscribe as unsubscribeMediaQuery } from '../utils/media-query';

const isTiny = ref(mediaSizes()['screen-x-small']);
const mediaQueryHandler = () => {
	isTiny.value = mediaSizes()['screen-x-small'];
};
onMounted(() => {
	subscribeMediaQuery(mediaQueryHandler);
});
onUnmounted(() => {
	unsubscribeMediaQuery(mediaQueryHandler);
});

const props = defineProps([
	'initial',					// Initial languages array data
	'label',					// Text label used for title (optional)
	'validationGroup',			// Validation group to use, required for validation to work!
]);
const { label, validationGroup } = toRefs(props);
const emits = defineEmits(["valueChanged"]);
const languageRows = ref([]);

const validationError = ref(false);
const validationCallbacks = [];
const validationAdapter = {
	getValue: () => {
		return languageRows.value?.length ?? 0;
	},
	applyValidationResults: (e) => {
		validationError.value = !e.isValid;
	},
	validationRequestsCallbacks: validationCallbacks,
};
const validateCallback = ({value}) => {
	return value > 0;
};
const revalidateCallback = () => {
	validationCallbacks.forEach((func) => {
		func();
	});
};

const languagesData = new DataSource({
	store: languages, pageSize: 10, paginate: true, searchExpr: [ 'name', 'native', 'code' ],
	sort: (l) => (l.code == 'en' || l.code == 'tl') ? '!' : l.name.toLowerCase()
});
const languageLevels = [ 'Beginner', 'Intermediate', 'Advanced', 'Fluent' ];
const languageDisplayExpr = (item) => item ? (item?.name + ' (' + item?.native + ')') : null;

const addLanguageCode = ref('');
const addLanguageLevel = ref('');
const addLanguageBtn = () => {
	if (!addLanguageCode.value || !addLanguageLevel.value) {
		return notify({ message: "Please select both language and proficiency level!", position: 'center' }, "error", 2000);
	}
	// Add language, or replace if already present
	let existingIdx = languageRows.value.findIndex(l => l.code == addLanguageCode.value);
	if (existingIdx != -1) {
		languageRows.value[existingIdx].level = addLanguageLevel.value;
	} else {
		languageRows.value.push({
			code: addLanguageCode.value,
			label: languages.find(l => l.code == addLanguageCode.value)?.name ?? addLanguageCode.value,
			level: addLanguageLevel.value,
		});
	}
	addLanguageCode.value = '';
	addLanguageLevel.value = '';
	revalidateCallback();
	emits('valueChanged', languageRows);
};

const deleteLanguageBtn = (code) => {
	languageRows.value = languageRows.value.filter(l => l.code != code);
	revalidateCallback();
	emits('valueChanged', languageRows);
};

// Copy over initial languages if provided; this is a one-way binding only!
onMounted(() => {
	if (props.initial && Array.isArray(props.initial)) {
		props.initial.forEach(l => languageRows.value.push({ ...l }));
	}
});

</script>

<style lang="scss">
@import '../themes/generated/variables.base.scss';

fieldset.language-validation-error {
	border-left: 5px solid #f44336;
}
div.language-validation-error {
	margin-top: 5px;
	color: #f44336;
}
.language-row {
	padding: 10px 20px;
	border: 1px solid $base-border-color;
	border-left: 15px solid $base-border-color;
	margin-bottom: 1em;
	.language-level {
		background: $base-border-color;
		border: 1px solid $base-border-color;
		border-radius: 5px;
		padding: 2px 5px;
		margin-left: 10px;
	}
	.language-remove {
		cursor: pointer;
		float: right;
		margin-top: -4px;
		color: #f44336;
	}
}
</style>
